import { showToast } from "@/components/feedback"
import { BottomSheet } from "@/components/modals/BottomSheet"
import { Button, Field, RatingStars, SheetBody, SheetFooter, Text, TextArea } from "@/components/ui"
import {
  MAX_COMMENT_LENGTH,
  submitOrderFeedback,
  type FeedbackFailure,
  type FeedbackSubmission,
  type OrderFeedback,
} from "@/domain/order-feedback/order-feedback"
import { feedbackMessage, type FeedbackMessageKey } from "@/lib/i18n/feedback-messages"
import { useEffect, useState } from "react"

type OrderRatingSheetProps = {
  orderId: string
  displayId: string
  open: boolean
  initialStars?: number
  onOpenChange: (open: boolean) => void
  onRated: (feedback: OrderFeedback) => void
  /** Replaces the logged-in submission, e.g. the public tracking page rating by token. */
  submit?: (stars: number, comment: string) => Promise<FeedbackSubmission>
}

const STAR_LABELS: FeedbackMessageKey[] = ["star1", "star2", "star3", "star4", "star5"]

/** Bottom sheet with 5 tappable stars and an optional comment; submits once and reports back the stored rating. */
export function OrderRatingSheet({ orderId, displayId, open, initialStars = 0, onOpenChange, onRated, submit }: OrderRatingSheetProps) {
  const [stars, setStars] = useState(initialStars)
  const [comment, setComment] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setStars((current) => current || initialStars)
  }, [open, initialStars])

  async function handleSubmit() {
    if (stars < 1 || submitting) return
    setSubmitting(true)
    try {
      const { feedback, alreadyRated } = submit
        ? await submit(stars, comment.trim().slice(0, MAX_COMMENT_LENGTH))
        : await submitOrderFeedback(orderId, stars, comment)
      onRated(feedback)
      if (alreadyRated) showToast.info(feedbackMessage("alreadyRated"))
      else showToast.success(feedbackMessage("thanks"))
      onOpenChange(false)
    } catch (failure) {
      showToast.error(feedbackMessage(typeof failure === "string" ? (failure as FeedbackFailure) : "failed"))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={feedbackMessage("sheetTitle")}
      description={`Pedido #${displayId.toUpperCase()} · ${feedbackMessage("sheetSubtitle")}`}
      onClose={() => onOpenChange(false)}
    >
      <SheetBody className="mx-auto w-full max-w-md">
        <StarPicker value={stars} onChange={setStars} />
        <Text size="sm" weight="semibold" tone="muted" align="center" className="mt-1 h-5" aria-live="polite">
          {stars > 0 ? feedbackMessage(STAR_LABELS[stars - 1]) : ""}
        </Text>

        <Field
          id={`rating-comment-${orderId}`}
          label={feedbackMessage("commentLabel")}
          counter={{ current: comment.length, max: MAX_COMMENT_LENGTH }}
          className="mt-4"
        >
          <TextArea
            className="min-h-24"
            rows={3}
            maxLength={MAX_COMMENT_LENGTH}
            value={comment}
            placeholder={feedbackMessage("commentPlaceholder")}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>
      </SheetBody>
      <SheetFooter className="mx-auto w-full max-w-md">
        <Button
          fullWidth
          size="lg"
          disabled={stars < 1}
          isLoading={submitting}
          onClick={handleSubmit}
          data-testid="order-rating-submit"
        >
          {submitting ? feedbackMessage("submitting") : feedbackMessage("submit")}
        </Button>
      </SheetFooter>
    </BottomSheet>
  )
}

/** Five tappable stars forming a radio group; the arrow keys move the choice. */
export function StarPicker({ value, onChange }: { value: number; onChange: (stars: number) => void }) {
  return (
    <RatingStars
      size="lg"
      value={value}
      onChange={onChange}
      label={feedbackMessage("sheetTitle")}
      starLabel={(n) => feedbackMessage("starAria", { n })}
      className="mt-5 justify-center"
    />
  )
}
